import {
  ArrowRight,
  Shield,
  BarChart3,
  GitBranch,
  Users,
  ClipboardCheck,
  Zap,
  CheckCircle2,
  ChevronRight,
  Layers,
  Target,
  TrendingUp,
  Lock,
  Globe,
  FileText,
  Bell,
  Sparkles,
  UserCheck,
  Clock,
  ExternalLink,
  ListTodo,
  GitPullRequestArrow,
  Compass,
} from "lucide-react";

export function meta() {
  return [
    { title: "Orchestrate — Programme & Project Governance Platform" },
    {
      name: "description",
      content:
        "Programme and project management with built-in stage-gate governance, RAAIDD logs, resource management, and the APTIM-Solutions Input Management Framework. Replace spreadsheet chaos with structured delivery.",
    },
  ];
}

// ─── Pricing tiers ───
const TIERS = [
  {
    name: "Stakeholder",
    price: "Free",
    period: "for client users",
    description:
      "Read-only access for client sponsors, stakeholders, and reviewers. No barriers to adoption.",
    features: [
      "Dashboard & status views",
      "Gate decision review",
      "RAAIDD log visibility",
      "Comment on items",
      "Steering committee reports",
      "Unlimited stakeholders",
    ],
    cta: "Included with every plan",
    ctaDisabled: true,
    highlight: false,
  },
  {
    name: "Professional",
    price: "Bespoke",
    period: "per user / month",
    description:
      "For delivery teams that need full governance, resource management, and multi-project oversight.",
    features: [
      "Unlimited projects & programmes",
      "Full governance suite (73 deliverables)",
      "Product sign-off workflow (I-R-A)",
      "Weighted stage gate scoring",
      "RAAIDD log with escalation & linked products",
      "Change request management",
      "Task tracking with effort management",
      "Time logging with budget integration",
      "RACI matrix auto-generation",
      "Steering committee reports (PDF & Word)",
      "Programme & portfolio dashboards",
      "Budget, benefits & resource tracking",
      "Custom phase templates",
      "Priority support",
    ],
    cta: "Get a quote",
    ctaDisabled: false,
    highlight: true,
  },
  {
    name: "Enterprise",
    price: "Bespoke",
    period: "per programme or organisation",
    description:
      "For large organisations with portfolio oversight, advanced security, and dedicated support.",
    features: [
      "Everything in Professional",
      "Unlimited users",
      "SSO (Entra ID / SAML)",
      "API access & integrations",
      "Multi-currency programmes",
      "External resource management",
      "Client portal (coming soon)",
      "Dedicated account manager",
      "SLA & on-boarding support",
    ],
    cta: "Contact sales",
    ctaDisabled: false,
    highlight: false,
  },
];

const FEATURES = [
  {
    icon: GitBranch,
    title: "7-Stage Lifecycle",
    description:
      "From Market Development through Continuous Improvement. Configurable phase templates that map to your delivery methodology.",
  },
  {
    icon: Shield,
    title: "Governance & Sign-Off",
    description:
      "73 management products with full I-R-A sign-off workflow. Initiator → Reviewer → Authoriser chain with quality criteria verification and audit trail.",
  },
  {
    icon: Target,
    title: "Stage Gate Quality Scoring",
    description:
      "Weighted criteria scored at every gate. Pass, Conditional, or Fail decisions with deliverable readiness checks and automatic phase advancement.",
  },
  {
    icon: ClipboardCheck,
    title: "RAAIDD Log & Change Requests",
    description:
      "Risks, Assumptions, Actions, Issues, and Dependencies with severity scoring, product linking, and comment threads. Linked change request workflow from draft through approval.",
  },
  {
    icon: ListTodo,
    title: "Task & Effort Tracking",
    description:
      "Tasks linked to governance deliverables and products with baseline, actual, and forecast effort. Quick-add issues and defects directly from task views.",
  },
  {
    icon: BarChart3,
    title: "Portfolio & Programme Dashboards",
    description:
      "Programme health cards, RAG distribution, budget variance, top risks, benefits pipeline, and upcoming gates — from portfolio level down to individual projects.",
  },
  {
    icon: FileText,
    title: "Steering Committee Reports",
    description:
      "One-click report generation at project and programme level. Full data snapshots with narrative, RAAIDD highlights, budget summary, and Word document export.",
  },
  {
    icon: Clock,
    title: "Time Logging & Budgets",
    description:
      "Weekly timesheet workflow with PM approval. Labour costs feed into budget actuals. Phase-level budgets, multi-currency support, and benefits tracking.",
  },
  {
    icon: UserCheck,
    title: "Resource Management",
    description:
      "Assign internal and external team members across projects. Auto-generated RACI matrix, role-based allocation, and cross-project resource visibility.",
  },
  {
    icon: Users,
    title: "Three-Tier Access Control",
    description:
      "Platform, Organisation, and Project roles with permission-gated UI. Multi-tenancy enforcement ensures complete client data isolation.",
  },
];

const PROOF_POINTS = [
  { value: "73", label: "Governance deliverables per project" },
  { value: "7", label: "Lifecycle phases with configurable templates" },
  { value: "30+", label: "Features shipped and production-ready" },
  { value: "<2s", label: "Dashboard load time target" },
];

const COMPARISON = [
  {
    feature: "Governance deliverables built-in",
    orchestrate: true,
    smartsheet: false,
    monday: false,
    teamwork: false,
    msProject: false,
  },
  {
    feature: "Product sign-off workflow (I-R-A)",
    orchestrate: true,
    smartsheet: false,
    monday: false,
    teamwork: false,
    msProject: false,
  },
  {
    feature: "Weighted stage gate scoring",
    orchestrate: true,
    smartsheet: false,
    monday: false,
    teamwork: false,
    msProject: false,
  },
  {
    feature: "RAAIDD log with product linking",
    orchestrate: true,
    smartsheet: false,
    monday: false,
    teamwork: false,
    msProject: false,
  },
  {
    feature: "Change request management",
    orchestrate: true,
    smartsheet: false,
    monday: false,
    teamwork: true,
    msProject: false,
  },
  {
    feature: "Auto-generated RACI matrix",
    orchestrate: true,
    smartsheet: false,
    monday: false,
    teamwork: false,
    msProject: false,
  },
  {
    feature: "Steering committee reports",
    orchestrate: true,
    smartsheet: false,
    monday: false,
    teamwork: false,
    msProject: false,
  },
  {
    feature: "Phase lifecycle management",
    orchestrate: true,
    smartsheet: false,
    monday: false,
    teamwork: false,
    msProject: true,
  },
  {
    feature: "Programme-level dashboards",
    orchestrate: true,
    smartsheet: true,
    monday: true,
    teamwork: false,
    msProject: false,
  },
  {
    feature: "Resource management",
    orchestrate: true,
    smartsheet: true,
    monday: true,
    teamwork: true,
    msProject: true,
  },
  {
    feature: "Time logging & approval",
    orchestrate: true,
    smartsheet: true,
    monday: true,
    teamwork: true,
    msProject: false,
  },
  {
    feature: "Multi-tenancy & data isolation",
    orchestrate: true,
    smartsheet: true,
    monday: true,
    teamwork: true,
    msProject: false,
  },
  {
    feature: "Budget & benefits tracking",
    orchestrate: true,
    smartsheet: true,
    monday: false,
    teamwork: false,
    msProject: false,
  },
  {
    feature: "Custom phase templates",
    orchestrate: true,
    smartsheet: false,
    monday: false,
    teamwork: false,
    msProject: false,
  },
];

const COMING_SOON = [
  {
    icon: ExternalLink,
    title: "Client Portal",
    description:
      "Dedicated branded login for client sponsors. Programme status, milestones, gate decisions, and document repository — without the noise.",
  },
  {
    icon: Bell,
    title: "In-App Notifications",
    description:
      "Real-time alerts for gate approvals, RAAIDD escalations, budget thresholds, approaching deadlines, and sign-off requests.",
  },
  {
    icon: GitPullRequestArrow,
    title: "Data Export",
    description:
      "Export RAAIDD logs, deliverables, budget data, and gate records as CSV, Excel, or PDF. No data lock-in.",
  },
  {
    icon: TrendingUp,
    title: "Earned Value Analysis",
    description:
      "CPI, SPI, and EAC metrics with AI-powered cost forecasting. Confidence intervals alongside traditional budget-at-completion.",
  },
  {
    icon: Sparkles,
    title: "AI-Powered Insights",
    description:
      "Gate readiness scoring, natural language RAAIDD entry, smart status report drafting, and anomaly alerting — powered by Claude.",
  },
  {
    icon: Globe,
    title: "Microsoft 365 Integration",
    description:
      "Entra ID SSO, Teams notifications for gate decisions and escalations, and SharePoint document storage.",
  },
];

export default function LandingPage({ authenticated = false }: { authenticated?: boolean }) {
  return (
    <div className="min-h-screen bg-white">
      {/* ─── Navigation ─── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-border-light">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-navy rounded-lg flex items-center justify-center">
              <Layers className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-bold text-navy tracking-tight">Orchestrate</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a
              href="#features"
              className="text-sm font-medium text-text-secondary hover:text-navy transition-colors"
            >
              Features
            </a>
            <a
              href="#comparison"
              className="text-sm font-medium text-text-secondary hover:text-navy transition-colors"
            >
              Compare
            </a>
            <a
              href="#pricing"
              className="text-sm font-medium text-text-secondary hover:text-navy transition-colors"
            >
              Pricing
            </a>
            <a
              href="#roadmap"
              className="text-sm font-medium text-text-secondary hover:text-navy transition-colors"
            >
              Roadmap
            </a>
            <a
              href="/survey"
              className="text-sm font-medium text-text-secondary hover:text-navy transition-colors"
            >
              Survey
            </a>
          </div>
          <div className="flex items-center gap-3">
            {authenticated ? (
              <a
                href="/orchestrate/programmes"
                className="text-sm font-semibold text-white bg-navy hover:bg-navy-light px-4 py-2 rounded-lg transition-colors"
              >
                Go to app
              </a>
            ) : (
              <>
                <a
                  href="/login"
                  className="text-sm font-semibold text-navy hover:text-accent-brand transition-colors"
                >
                  Sign in
                </a>
                <a
                  href="/login"
                  className="text-sm font-semibold text-white bg-navy hover:bg-navy-light px-4 py-2 rounded-lg transition-colors"
                >
                  Request access
                </a>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-b from-navy-faint/50 to-white">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-navy/5 text-navy text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-6">
            <Zap className="w-3.5 h-3.5" />
            Built for structured delivery teams
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-navy leading-tight mb-6">
            Stop managing projects <span className="text-accent-brand">in spreadsheets.</span>
          </h1>
          <p className="text-lg sm:text-xl text-text-secondary max-w-3xl mx-auto mb-10 leading-relaxed">
            Orchestrate is the programme and project governance platform that combines structured
            delivery management, quality-scored stage gates, product sign-off workflows, change
            request management, resource oversight, and full RAAIDD lifecycle — powered by the
            APTIM-Solutions Input Management Framework.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/login"
              className="inline-flex items-center gap-2 bg-navy text-white font-bold text-base px-8 py-3.5 rounded-xl hover:bg-navy-light transition-colors shadow-lg shadow-navy/20"
            >
              Request a demo
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#features"
              className="inline-flex items-center gap-2 text-navy font-semibold text-base px-8 py-3.5 rounded-xl border-2 border-navy/15 hover:border-navy/30 transition-colors"
            >
              See how it works
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
          <p className="text-sm text-text-muted mt-6">
            Already an APTIM-Solutions client? Orchestrate is included in your engagement.
          </p>
        </div>
      </section>

      {/* ─── Proof points ─── */}
      <section className="py-12 px-6 border-y border-border-light bg-surface">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {PROOF_POINTS.map((p) => (
            <div key={p.label} className="text-center">
              <div className="text-3xl font-extrabold text-navy mb-1">{p.value}</div>
              <div className="text-sm text-text-muted">{p.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Problem / Solution ─── */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-extrabold text-navy mb-4">
                Governance shouldn't be an afterthought.
              </h2>
              <p className="text-text-secondary leading-relaxed mb-6">
                Most project management tools bolt on governance as a reporting layer. Orchestrate
                was designed from day one around structured delivery control and stage-gate quality
                assurance — the governance structures that actually prevent project failure. No
                other tool combines configurable stage gates, formal product sign-off, change
                request management, and a first-class RAAIDD log in a single product.
              </p>
              <ul className="space-y-3">
                {[
                  "Phase templates with 73 governance deliverables",
                  "Product sign-off with I-R-A chain and audit trail",
                  "Weighted quality criteria at every stage gate",
                  "RAAIDD log linked to products with change request workflow",
                  "Steering committee reports at project and programme level",
                  "Auto-generated RACI matrix from deliverable assignments",
                  "Time logging with budget integration and PM approval",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-text">
                    <CheckCircle2 className="w-5 h-5 text-success shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-navy rounded-2xl p-8 text-white">
              <div className="text-xs font-bold uppercase tracking-wider text-white/60 mb-4">
                The Input Management Framework
              </div>
              <div className="space-y-4">
                {[
                  { phase: "Scope", stages: "Market Dev → Opportunity Qualification" },
                  { phase: "Discover", stages: "Discovery & Technical Readiness" },
                  { phase: "Design", stages: "Blueprint" },
                  { phase: "Deliver", stages: "Implementation" },
                  { phase: "Sustain", stages: "Hypercare → Continuous Improvement" },
                ].map((item) => (
                  <div key={item.phase} className="flex items-start gap-3">
                    <div className="w-20 shrink-0 text-xs font-bold uppercase tracking-wider text-accent-brand/80 pt-0.5">
                      {item.phase}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-white/90">{item.stages}</div>
                      <div className="h-1 bg-white/10 rounded-full mt-2">
                        <div
                          className="h-1 bg-accent-brand rounded-full"
                          style={{ width: "100%" }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Features ─── */}
      <section id="features" className="py-20 px-6 bg-surface">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-extrabold text-navy mb-3">
              Everything you need to deliver with governance.
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Purpose-built for programme delivery teams that need structure, not just task lists.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="bg-white rounded-xl p-6 border border-border-light hover:shadow-lg hover:shadow-navy/5 transition-shadow"
              >
                <div className="w-10 h-10 rounded-lg bg-navy-faint flex items-center justify-center mb-4">
                  <f.icon className="w-5 h-5 text-navy" />
                </div>
                <h3 className="text-base font-bold text-navy mb-2">{f.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Comparison ─── */}
      <section id="comparison" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-extrabold text-navy mb-3">
              Built for governance. Not retrofitted.
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              See how Orchestrate compares to general-purpose project management tools.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-navy/10">
                  <th className="text-left py-3 pr-4 font-semibold text-text-secondary">Feature</th>
                  <th className="text-center py-3 px-3 font-bold text-navy">Orchestrate</th>
                  <th className="text-center py-3 px-3 font-semibold text-text-muted">
                    SmartSheet
                  </th>
                  <th className="text-center py-3 px-3 font-semibold text-text-muted">Monday</th>
                  <th className="text-center py-3 px-3 font-semibold text-text-muted">Teamwork</th>
                  <th className="text-center py-3 px-3 font-semibold text-text-muted">
                    MS Project
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.feature} className="border-b border-border-light">
                    <td className="py-3 pr-4 text-text">{row.feature}</td>
                    <td className="text-center py-3 px-3">
                      {row.orchestrate ? (
                        <CheckCircle2 className="w-5 h-5 text-success mx-auto" />
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center py-3 px-3">
                      {row.smartsheet ? (
                        <CheckCircle2 className="w-5 h-5 text-text-muted mx-auto" />
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center py-3 px-3">
                      {row.monday ? (
                        <CheckCircle2 className="w-5 h-5 text-text-muted mx-auto" />
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center py-3 px-3">
                      {row.teamwork ? (
                        <CheckCircle2 className="w-5 h-5 text-text-muted mx-auto" />
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center py-3 px-3">
                      {row.msProject ? (
                        <CheckCircle2 className="w-5 h-5 text-text-muted mx-auto" />
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ─── Engagement model callout ─── */}
      <section className="py-16 px-6 bg-navy-faint/40">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-extrabold text-navy mb-3">
            Included with every APTIM-Solutions engagement.
          </h2>
          <p className="text-text-secondary max-w-2xl mx-auto mb-6">
            When you partner with APTIM-Solutions for programme delivery, Orchestrate access is
            bundled into your engagement — no separate licensing conversation, no per-seat
            negotiation. Your team and your client stakeholders get access from day one.
          </p>
          <a
            href="/login"
            className="inline-flex items-center gap-2 text-navy font-semibold text-sm border-2 border-navy/15 hover:border-navy/30 px-6 py-2.5 rounded-xl transition-colors"
          >
            Learn about APTIM-Solutions engagements
            <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </div>
      </section>

      {/* ─── Pricing ─── */}
      <section id="pricing" className="py-20 px-6 bg-surface">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-extrabold text-navy mb-3">
              Flexible pricing that fits how you work.
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              Standalone licensing tailored to your team size and delivery needs. Client stakeholder
              access is always free.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TIERS.map((tier) => (
              <div
                key={tier.name}
                className={`rounded-2xl p-8 flex flex-col ${
                  tier.highlight
                    ? "bg-navy text-white ring-4 ring-navy/20 shadow-xl shadow-navy/15 scale-[1.02]"
                    : "bg-white border border-border-light"
                }`}
              >
                <div className="mb-6">
                  <h3
                    className={`text-lg font-bold mb-1 ${tier.highlight ? "text-white" : "text-navy"}`}
                  >
                    {tier.name}
                  </h3>
                  <p
                    className={`text-sm mb-4 ${tier.highlight ? "text-white/70" : "text-text-secondary"}`}
                  >
                    {tier.description}
                  </p>
                  <div className="flex items-baseline gap-1">
                    <span
                      className={`text-4xl font-extrabold ${tier.highlight ? "text-white" : "text-navy"}`}
                    >
                      {tier.price}
                    </span>
                    {tier.price !== "Free" && (
                      <span
                        className={`text-sm ${tier.highlight ? "text-white/60" : "text-text-muted"}`}
                      >
                        {tier.period}
                      </span>
                    )}
                  </div>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle2
                        className={`w-4 h-4 shrink-0 mt-0.5 ${tier.highlight ? "text-accent-brand" : "text-success"}`}
                      />
                      <span className={tier.highlight ? "text-white/90" : "text-text"}>{f}</span>
                    </li>
                  ))}
                </ul>
                {tier.ctaDisabled ? (
                  <div className="block text-center font-semibold text-sm py-3 rounded-xl bg-surface-2 text-text-muted">
                    {tier.cta}
                  </div>
                ) : (
                  <a
                    href="/login"
                    className={`block text-center font-bold text-sm py-3 rounded-xl transition-colors ${
                      tier.highlight
                        ? "bg-white text-navy hover:bg-white/90"
                        : "bg-navy text-white hover:bg-navy-light"
                    }`}
                  >
                    {tier.cta}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Coming Soon ─── */}
      <section id="roadmap" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-extrabold text-navy mb-3">On the roadmap.</h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              With 30+ features shipped, we're building the next wave — AI-powered insights, client
              portals, and enterprise integrations.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {COMING_SOON.map((item) => (
              <div
                key={item.title}
                className="relative rounded-xl p-6 border border-dashed border-navy/20 bg-navy-faint/30"
              >
                <div className="absolute top-4 right-4">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-accent-brand bg-accent-brand/10 px-2 py-0.5 rounded-full">
                    Soon
                  </span>
                </div>
                <item.icon className="w-8 h-8 text-navy/60 mb-3" />
                <h3 className="text-sm font-bold text-navy mb-1.5">{item.title}</h3>
                <p className="text-xs text-text-secondary leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Trust / Security ─── */}
      <section className="py-20 px-6 bg-surface">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl font-extrabold text-navy mb-10">
            Enterprise-grade security. Built in.
          </h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
            {[
              {
                icon: Lock,
                title: "SSO & MFA",
                description:
                  "Enterprise identity management via Auth0 with multi-factor authentication",
              },
              {
                icon: Shield,
                title: "Data Isolation",
                description: "Multi-tenant architecture with strict organisation boundaries",
              },
              {
                icon: Globe,
                title: "EU Data Residency",
                description: "Hosted in Ireland with full GDPR compliance",
              },
              {
                icon: TrendingUp,
                title: "Audit Trail",
                description: "Every action logged with user, timestamp, and context",
              },
            ].map((item) => (
              <div key={item.title} className="p-5">
                <item.icon className="w-8 h-8 text-navy mx-auto mb-3" />
                <h4 className="text-sm font-bold text-navy mb-1">{item.title}</h4>
                <p className="text-xs text-text-muted">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-20 px-6 bg-navy">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-extrabold text-white mb-4">
            Ready to bring governance to your project delivery?
          </h2>
          <p className="text-white/70 mb-8 text-lg">
            Join teams that have replaced spreadsheet-driven project management with structured,
            auditable delivery.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/login"
              className="inline-flex items-center gap-2 bg-white text-navy font-bold text-base px-8 py-3.5 rounded-xl hover:bg-white/90 transition-colors"
            >
              Request a demo
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <p className="text-white/40 text-sm mt-6">
            APTIM-Solutions clients get access included in their engagement.
          </p>
        </div>
      </section>

      {/* ─── Survey CTA ─── */}
      <section className="py-16 px-6 bg-surface border-t border-border-light">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-8 md:gap-12">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 bg-navy/5 text-navy text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-4">
              <Compass className="w-3.5 h-3.5" />
              Disrupting the Future
            </div>
            <h2 className="text-2xl font-extrabold text-navy mb-3">
              How future-ready is your organisation?
            </h2>
            <p className="text-text-secondary text-sm leading-relaxed">
              Take the Disrupting the Future survey — a research-backed assessment developed by
              APTIM-Solutions. Benchmark your organisation across six
              dimensions of future-readiness and receive your score instantly.
            </p>
          </div>
          <div className="shrink-0">
            <a
              href="/survey"
              className="inline-flex items-center gap-2 bg-navy text-white font-bold text-sm px-6 py-3 rounded-xl hover:bg-navy-light transition-colors shadow-lg shadow-navy/15"
            >
              Take the survey
              <ArrowRight className="w-4 h-4" />
            </a>
            <p className="text-xs text-text-muted mt-2 text-center">~25 min · Anonymous · Free</p>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="py-10 px-6 border-t border-border-light bg-white">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-navy rounded flex items-center justify-center">
              <Layers className="w-3 h-3 text-white" />
            </div>
            <span className="text-sm font-bold text-navy">Orchestrate</span>
            <span className="text-xs text-text-muted ml-1">by APTIM-Solutions</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-text-muted">
            <a href="/privacy" className="hover:text-navy transition-colors">
              Privacy Policy
            </a>
            <a href="/terms" className="hover:text-navy transition-colors">
              Terms & Conditions
            </a>
            <a href="/dpa" className="hover:text-navy transition-colors">
              DPA
            </a>
            <a href="/company" className="hover:text-navy transition-colors">
              Company Details
            </a>
          </div>
          <span className="text-xs text-text-muted">
            &copy; {new Date().getFullYear()} APTIM-Solutions Ltd. All rights reserved.
          </span>
        </div>
      </footer>
    </div>
  );
}
